import { jsPDF } from 'jspdf';
import 'jspdf-autotable';

export const exportToPDF = (shiftData, options = {}) => {
  const doc = new jsPDF();
  const { fileName = 'shift-report.pdf', includeCharts = false } = options;

  // Add title
  doc.setFontSize(20);
  doc.setTextColor(102, 126, 234);
  doc.text('Shift Schedule Report', 14, 22);

  // Add metadata
  doc.setFontSize(10);
  doc.setTextColor(100);
  doc.text(`Generated: ${new Date().toLocaleDateString()}`, 14, 30);
  doc.text(`File: ${shiftData.fileName || 'N/A'}`, 14, 36);

  // Add summary statistics
  doc.setFontSize(14);
  doc.setTextColor(0);
  doc.text('Summary Statistics', 14, 48);

  const summaryData = [
    ['Metric', 'Value'],
    ['Staff Count', (shiftData.staffCount || 0).toString()],
    ['Total Hours', (shiftData.totalHours || 0).toFixed(1)],
    ['Standard Hours', (shiftData.totalStandardHours || 0).toFixed(1)],
    ['Overtime Hours', (shiftData.totalOvertimeHours || 0).toFixed(1)],
    ['Night Work Hours', (shiftData.totalNightWorkHours || 0).toFixed(1)]
  ];

  doc.autoTable({
    startY: 52,
    head: [summaryData[0]],
    body: summaryData.slice(1),
    theme: 'grid',
    headStyles: { fillColor: [102, 126, 234] },
    margin: { left: 14, right: 14 }
  });

  // Add staff details if available
  if (shiftData.processedData && shiftData.processedData.staffGroups) {
    const staffGroups = shiftData.processedData.staffGroups;

    doc.addPage();
    doc.setFontSize(14);
    doc.text('Staff Details', 14, 22);

    const staffData = [
      ['Staff Code', 'Name', 'Hours', 'Overtime', 'Night Work']
    ];

    staffGroups.forEach(staff => {
      staffData.push([
        staff.staffCode || 'N/A',
        staff.staffName || 'N/A',
        (staff.totals?.hours || 0).toFixed(1),
        (staff.totals?.overtimeHours || 0).toFixed(1),
        (staff.totals?.nightWorkHours || 0).toFixed(1)
      ]);
    });

    doc.autoTable({
      startY: 26,
      head: [staffData[0]],
      body: staffData.slice(1),
      theme: 'striped',
      headStyles: { fillColor: [102, 126, 234] },
      margin: { left: 14, right: 14 }
    });
  }

  // Add footer
  const pageCount = doc.internal.getNumberOfPages();
  for (let i = 1; i <= pageCount; i++) {
    doc.setPage(i);
    doc.setFontSize(8);
    doc.setTextColor(150);
    doc.text(
      `Page ${i} of ${pageCount}`,
      doc.internal.pageSize.getWidth() / 2,
      doc.internal.pageSize.getHeight() - 10,
      { align: 'center' }
    );
  }

  // Save the PDF
  doc.save(fileName);
};

export const exportMultipleShiftsToPDF = (shifts, fileName = 'shifts-summary.pdf') => {
  const doc = new jsPDF();

  // Add title
  doc.setFontSize(20);
  doc.setTextColor(102, 126, 234);
  doc.text('Shifts Summary Report', 14, 22);

  // Add metadata
  doc.setFontSize(10);
  doc.setTextColor(100);
  doc.text(`Generated: ${new Date().toLocaleDateString()}`, 14, 30);
  doc.text(`Total Uploads: ${shifts.length}`, 14, 36);

  // Calculate grand totals
  const grandTotals = shifts.reduce((acc, shift) => ({
    staffCount: acc.staffCount + (shift.staffCount || 0),
    totalHours: acc.totalHours + (shift.totalHours || 0),
    overtimeHours: acc.overtimeHours + (shift.totalOvertimeHours || 0),
    nightHours: acc.nightHours + (shift.totalNightWorkHours || 0)
  }), { staffCount: 0, totalHours: 0, overtimeHours: 0, nightHours: 0 });

  // Add summary
  const summaryData = [
    ['Metric', 'Total'],
    ['Total Staff Processed', grandTotals.staffCount.toString()],
    ['Total Hours', grandTotals.totalHours.toFixed(1)],
    ['Total Overtime', grandTotals.overtimeHours.toFixed(1)],
    ['Total Night Work', grandTotals.nightHours.toFixed(1)]
  ];

  doc.autoTable({
    startY: 42,
    head: [summaryData[0]],
    body: summaryData.slice(1),
    theme: 'grid',
    headStyles: { fillColor: [102, 126, 234] }
  });

  // Add individual uploads
  const uploadsData = [
    ['File Name', 'Date', 'Staff', 'Hours', 'Overtime']
  ];

  shifts.forEach(shift => {
    uploadsData.push([
      shift.fileName?.substring(0, 30) || 'N/A',
      new Date(shift.uploadDate).toLocaleDateString(),
      (shift.staffCount || 0).toString(),
      (shift.totalHours || 0).toFixed(1),
      (shift.totalOvertimeHours || 0).toFixed(1)
    ]);
  });

  let finalY = doc.lastAutoTable.finalY || 80;
  doc.setFontSize(14);
  doc.text('Upload Details', 14, finalY + 10);

  doc.autoTable({
    startY: finalY + 14,
    head: [uploadsData[0]],
    body: uploadsData.slice(1),
    theme: 'striped',
    headStyles: { fillColor: [102, 126, 234] }
  });

  // Add footer
  const pageCount = doc.internal.getNumberOfPages();
  for (let i = 1; i <= pageCount; i++) {
    doc.setPage(i);
    doc.setFontSize(8);
    doc.setTextColor(150);
    doc.text(
      `Page ${i} of ${pageCount}`,
      doc.internal.pageSize.getWidth() / 2,
      doc.internal.pageSize.getHeight() - 10,
      { align: 'center' }
    );
  }

  doc.save(fileName);
};

export const exportToCSV = (data, fileName = 'export.csv') => {
  if (!data || data.length === 0) {
    alert('No data to export');
    return;
  }

  const headers = Object.keys(data[0]);
  const csvContent = [
    headers.join(','),
    ...data.map(row =>
      headers.map(header => {
        const value = row[header];
        return typeof value === 'string' && value.includes(',')
          ? `"${value}"`
          : value;
      }).join(',')
    )
  ].join('\n');

  const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
  const link = document.createElement('a');
  const url = URL.createObjectURL(blob);

  link.setAttribute('href', url);
  link.setAttribute('download', fileName);
  link.style.visibility = 'hidden';

  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
};
